/**
 * Custom Forms — client-side model + helpers.
 *
 * Mirrors backend shapes defined in `commons/src/main/groovy/com/kerkton/orna/viewModel/form/*`.
 * See `docs/house-check-request/implementation-plan.md` for the design (§2.3, §4.1, §5).
 *
 * Phase E scope: types + factories + enum-like constants. UI components live in Phase F/G.
 */

import type {
  AttachableRecordType,
  CreateFormSchemaCommandData,
  FormFieldData,
  FormFieldType,
  FormHostDisplayData,
  FormInstanceData,
  FormInstanceUpsertResultData,
  FormReindexImpactData,
  FormReindexResultData,
  FormSchemaData,
  FormSchemaDeleteResultData,
  FormSchemaSaveResultData,
  FormSchemaValidationResultData,
  FormSchemaVersionDetailData,
  FormSchemaWarningCode,
  FormSchemaWarningData,
  FormSearchOp,
  FormSearchPredicateData,
  FormSearchRequestData,
  FormSearchResultData,
  FormSearchResultPageData,
  FormSectionData,
  FormSummaryData,
  FormSummaryFieldData,
  ToggleSearchableCommandData,
  UpdateFormSchemaCommandData,
  UpdateFormSchemaMetaCommandData,
  UpsertFormInstanceCommandData,
} from '@/services/api-types';

// Re-export so callers can `import type { ... } from '@/models/form-schema'`
export type {
  AttachableRecordType,
  CreateFormSchemaCommandData,
  FormFieldData,
  FormFieldType,
  FormHostDisplayData,
  FormInstanceData,
  FormInstanceUpsertResultData,
  FormReindexImpactData,
  FormReindexResultData,
  FormSchemaData,
  FormSchemaDeleteResultData,
  FormSchemaSaveResultData,
  FormSchemaValidationResultData,
  FormSchemaVersionDetailData,
  FormSchemaWarningCode,
  FormSchemaWarningData,
  FormSearchOp,
  FormSearchPredicateData,
  FormSearchRequestData,
  FormSearchResultData,
  FormSearchResultPageData,
  FormSectionData,
  FormSummaryData,
  FormSummaryFieldData,
  ToggleSearchableCommandData,
  UpdateFormSchemaCommandData,
  UpdateFormSchemaMetaCommandData,
  UpsertFormInstanceCommandData,
};

// ── Enum-like constants ──────────────────────────────────────────────

export const ATTACH_TO_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'CLIENT', label: 'Client records' },
  { value: 'CASE_REPORT', label: 'Case reports' },
];

export const ATTACH_TO_LABELS: Record<string, string> = Object.fromEntries(
  ATTACH_TO_OPTIONS.map((o) => [o.value, o.label]),
);

/** Case report forms are not part of Records Search; the backend rejects searchable fields there. */
export function attachToSupportsSearchable(attachTo: string): boolean {
  return attachTo !== 'CASE_REPORT';
}

export const FORM_FIELD_TYPE = {
  text: 'text',
  textarea: 'textarea',
  date: 'date',
  phone: 'phone',
  email: 'email',
  number: 'number',
  radio: 'radio',
  select: 'select',
  checkboxGroup: 'checkboxGroup',
  table: 'table',
  computed: 'computed',
} as const;

/** Types that can appear in a table column (scalar only — no nested tables, no computed). */
export const TABLE_COLUMN_FORM_FIELD_TYPES: readonly FormFieldType[] = [
  'text', 'textarea', 'date', 'phone', 'email', 'number', 'radio', 'select', 'checkboxGroup',
] as const;

/** Types that may be declared `searchable: true`. Plan §4.1 rule 13. */
export const SEARCHABLE_FORM_FIELD_TYPES: readonly FormFieldType[] = [
  'text', 'textarea', 'date', 'phone', 'email', 'number', 'radio', 'select',
] as const;

/** Types that may be declared `summary: true`. Plan §4.1 rule 12 — all scalars (not table). */
export const SUMMARY_FORM_FIELD_TYPES: readonly FormFieldType[] = [
  'text', 'textarea', 'date', 'phone', 'email', 'number', 'radio', 'select', 'checkboxGroup', 'computed',
] as const;

export const FORM_SEARCH_OP = {
  EQ: 'EQ',
  NEQ: 'NEQ',
  GT: 'GT',
  GTE: 'GTE',
  LT: 'LT',
  LTE: 'LTE',
  CONTAINS: 'CONTAINS',
  BETWEEN: 'BETWEEN',
} as const;

/** Which ops are valid for which field types. Plan §5 / D15 compatibility matrix. */
export const FORM_SEARCH_OP_COMPATIBILITY: Record<FormSearchOp, readonly FormFieldType[]> = {
  EQ:       ['text', 'textarea', 'date', 'phone', 'email', 'number', 'radio', 'select', 'computed'],
  NEQ:      ['text', 'textarea', 'date', 'phone', 'email', 'number', 'radio', 'select', 'computed'],
  CONTAINS: ['text', 'textarea', 'phone', 'email'],
  GT:       ['number', 'date', 'computed'],
  GTE:      ['number', 'date', 'computed'],
  LT:       ['number', 'date', 'computed'],
  LTE:      ['number', 'date', 'computed'],
  BETWEEN:  ['number', 'date', 'computed'],
} as const;

// ── Type-narrowing helpers ───────────────────────────────────────────

export function isSearchableField(field: FormFieldData): boolean {
  if (field.searchable !== true) return false;
  return SEARCHABLE_FORM_FIELD_TYPES.includes(field.type);
}

export function isSummaryField(field: FormFieldData): boolean {
  if (field.summary !== true) return false;
  return SUMMARY_FORM_FIELD_TYPES.includes(field.type);
}

export function isOpCompatible(op: FormSearchOp, type: FormFieldType): boolean {
  return FORM_SEARCH_OP_COMPATIBILITY[op].includes(type);
}

/** Flatten sections → fields. Does NOT descend into table columns. */
export function allFields(sections: FormSectionData[] | null | undefined): FormFieldData[] {
  if (!sections) return [];
  const out: FormFieldData[] = [];
  for (const section of sections) {
    for (const field of section.fields ?? []) {
      out.push(field);
    }
  }
  return out;
}

/** Field-key → field map. Useful for visibility / formula evaluation. */
export function fieldMap(sections: FormSectionData[] | null | undefined): Map<string, FormFieldData> {
  const map = new Map<string, FormFieldData>();
  for (const field of allFields(sections)) {
    if (field.key) map.set(field.key, field);
  }
  return map;
}

// ── Factories ────────────────────────────────────────────────────────

export function createFormField(data?: Partial<FormFieldData>): FormFieldData {
  return {
    key: '',
    label: '',
    type: 'text',
    ...data,
  };
}

export function createFormSection(data?: Partial<FormSectionData>): FormSectionData {
  return {
    id: '',
    title: '',
    fields: [],
    ...data,
  };
}

export function createCreateFormSchemaCommand(
  data?: Partial<CreateFormSchemaCommandData>,
): CreateFormSchemaCommandData {
  return {
    name: '',
    attachTo: 'CLIENT',
    sections: [],
    ...data,
  };
}

export function createUpsertFormInstanceCommand(
  data?: Partial<UpsertFormInstanceCommandData>,
): UpsertFormInstanceCommandData {
  return {
    id: null,
    formSchemaId: null,
    recordType: 'CLIENT',
    recordId: 0,
    values: {},
    ...data,
  };
}

export function createFormSearchRequest(
  data?: Partial<FormSearchRequestData>,
): FormSearchRequestData {
  return {
    schemaId: 0,
    attachTo: 'CLIENT',
    predicates: [],
    page: 0,
    pageSize: 50,
    ...data,
  };
}
